import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {Link} from "react-router-dom";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {
	textColumn,
	tooltipColumn
} from "../../../components/common/grid/Columns";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {EXISTS_PARTNER_HIERARCHY, PARTNER_HIERARCHY_OVERVIEW, PUT_PARTNER_HIERARCHY} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {translateFunction} from "../../../localization/TranslationService";
import {GridColDef, GridPagination} from "@mui/x-data-grid-pro";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {cloneMutationData} from "../Utils";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {PartnerHierarchy} from "../models/PartnerHierarchy";
import {linkPartnerHierarchyFor} from "./PartnerHierarchyDetails";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const PartnerHierarchyOverview = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Typography color="text.primary">{translate("Partner Hierarchy")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<PartnerHierarchyOverviewContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

export function PartnerHierarchyOverviewContent() {
	const [resetKey, setResetKey] = useState(0);
	const updateGrid = () => setResetKey(resetKey + 1);
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Open"),
			renderCell: (params) => (
				<Link to={linkPartnerHierarchyFor(params.row.id)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "parent",
			headerName: translate("Parent"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];

	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);

	return <GraphQlDataGrid
		rows={[]}
		query={PARTNER_HIERARCHY_OVERVIEW}
		graphFunction={"partnerHierarchy"}
		client={graphQLServices.productsClient}
		rowsPerPageOptions={[50, 100]}
		initialPageSize={50}
		columns={columns}
		getRowId={row => row.clientName}
		queryVariables={{stage, resetKey}}
		slots={{
			footer: CustomFooterFactory(updateGrid)
		}}
	/>;
}

async function exists(item: PartnerHierarchy): Promise<boolean> {

	const existsItems = await graphQLServices.productsClient.query(
		{
			query: EXISTS_PARTNER_HIERARCHY,
			variables: {
				stage: productsEditorTargetStage,
				clientName: item.clientName,
			},
			fetchPolicy: "no-cache"
		}
	);

	const nodes: any[] = existsItems.data.partnerHierarchy.nodes;
	return nodes.length > 0;
}


async function saveChanges(item: PartnerHierarchy) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PARTNER_HIERARCHY,
		variables: {
			stage: productsEditorTargetStage,
			partnerHierarchy: cloneMutationData(item),
		}
	});
}

function CustomFooterFactory(updateGrid: () => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);

		const [clientName, setClientName] = useState("");
		const [parent, setParent] = useState<string | null>(null);

		const handleClickOpen = () => setOpen(true);
		const handleClose = () => setOpen(false);

		const handleCreate = async () => {
			const id = crypto.randomUUID();
			const item = {id, clientName, parent} as PartnerHierarchy;
			item.clientName = item.clientName.toLowerCase().trim();
			item.parent = item.parent?.toLowerCase()?.trim() ?? null;
			await exists(item).then(async (x) => {
				if (x) {
					throw new Error(`Item already exists`);
				}

				await saveChanges(item);
				setOpen(false);
				updateGrid();
			});
		};

		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
				>{translate("Add")}</Button>
			</Stack>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Partner Hierarchy")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<TextField
							margin="dense"
							label={translate("Client Name")}
							fullWidth
							variant="standard"
							required
							value={clientName}
							onChange={event => {
								setClientName(event.target.value);
							}}
						/>
						<TextField
							margin="dense"
							label={translate("Parent")}
							fullWidth
							variant="standard"
							required
							value={parent ?? ""}
							onChange={event => {
								setParent(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									defaultText={translate("Create")}
									inProgressText={translate("Creating")}
									completedText={translate("Created")}
									resetKeys={[clientName, parent]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
		</Stack>;
	};
}

